/**
 * The signed-in admin's own profile over HTTP (/api/admin-profile): their
 * timezone, which dates their games' public results links.
 *
 * Modelled on ./adminManagementApi.ts (raw fetch + credentials).
 */

import { API_URL } from '../config';

export interface AdminProfile {
  username: string;
  /** IANA name, or null (UTC). */
  timezone: string | null;
}

async function readProfile(response: Response, fallback: string): Promise<AdminProfile> {
  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: fallback }));
    throw new Error(data.error || fallback);
  }
  return response.json();
}

/** @endpoint GET /api/admin-profile */
export async function getAdminProfile(): Promise<AdminProfile> {
  const response = await fetch(`${API_URL}/api/admin-profile`, { credentials: 'include' });
  return readProfile(response, 'Failed to load your profile');
}

/**
 * Set the timezone (null = UTC). `guess`: the browser's, stored only while
 * none is set.
 * @endpoint PATCH /api/admin-profile
 */
export async function updateAdminTimezone(timezone: string | null, guess = false): Promise<AdminProfile> {
  const response = await fetch(`${API_URL}/api/admin-profile`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ timezone, guess }),
  });
  return readProfile(response, 'Failed to save your timezone');
}

/** The browser's timezone ('Australia/Sydney'), or null when it can't tell. */
export function browserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** Every timezone the browser knows, for the picker (empty when it can't list them). */
export function browserTimezones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: 'timeZone') => string[] };
  try {
    return intl.supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
}
